import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchJson, postJson } from '../api';
import { trackBoost, trackBoostView } from '../lib/analytics';
import { formatCount, plural } from '../lib/format';

export const BOOST_MIN = 5;
export const BOOST_MAX = 200;
const QUICK = [10, 25, 50, 100];

/**
 * Boost sheet (spec §1 "Boost"): pick how many votes you want (5–200), the
 * cost is one credit per vote, POST /polls/:id/boost. A 402 means not enough
 * credits — say so and point at /credits.
 */
export default function BoostSheet({ poll, open, onClose, onBoosted }) {
  const [votes, setVotes] = useState(10);
  const [balance, setBalance] = useState(null);
  const [balanceError, setBalanceError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null); // null | { kind: 'credits' | 'other', needed?, credits? }
  const dialogRef = useRef(null);
  const firstRef = useRef(null);
  const openerRef = useRef(null);
  // Parents pass inline handlers; read them through refs so the open effect
  // runs once per opening, not on every parent render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    setError(null);
    setBalanceError(false);
    trackBoostView({ poll_id: poll.id });
    let cancelled = false;
    fetchJson('/me/credits')
      .then((d) => { if (!cancelled) setBalance(Number.isFinite(d?.credits) ? d.credits : null); })
      .catch(() => { if (!cancelled) { setBalance(null); setBalanceError(true); } });
    const t = setTimeout(() => firstRef.current?.focus(), 0);
    function onKey(e) {
      if (e.key === 'Escape') onCloseRef.current();
      if (e.key === 'Tab' && dialogRef.current) {
        // Keep focus inside the sheet.
        const nodes = dialogRef.current.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])');
        const list = Array.from(nodes).filter((n) => !n.disabled);
        if (!list.length) return;
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      cancelled = true;
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      if (openerRef.current && openerRef.current.focus) openerRef.current.focus();
    };
  }, [open, poll.id]);

  if (!open) return null;

  const cost = votes;
  const short = balance !== null && cost > balance;

  function clampVotes(v) {
    const n = Math.round(Number(v) || 0);
    return Math.max(BOOST_MIN, Math.min(BOOST_MAX, n));
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const data = await postJson(`/polls/${poll.id}/boost`, { votes: cost });
      trackBoost(cost, { poll_id: poll.id });
      if (Number.isFinite(data?.credits)) setBalance(data.credits);
      onBoosted?.(data?.boost || { votes_wanted: cost, votes_delivered: 0 }, data?.credits);
      onClose();
    } catch (err) {
      if (err.status === 402) {
        const body = err.body || {};
        if (Number.isFinite(body.credits)) setBalance(body.credits);
        setError({ kind: 'credits', needed: body.needed, credits: body.credits });
      } else if (err.status === 401) {
        setError({ kind: 'auth' });
      } else {
        setError({ kind: 'other' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="boost-title"
        className="pb-safe relative w-full max-w-md rounded-t-2xl bg-voto-chrome p-5 shadow-sheet sm:rounded-2xl"
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-voto-line sm:hidden" aria-hidden="true" />
        <h2 id="boost-title" className="text-lg font-semibold text-white">Boost this poll</h2>
        <p className="mt-1 text-sm text-voto-muted">
          Boost · 1 credit per vote. Your poll goes to the top of the Voting Booth until the votes arrive.
        </p>

        <form onSubmit={submit} className="mt-4">
          <label htmlFor="boost-votes" className="flex items-baseline justify-between text-sm text-voto-fg">
            <span>Votes wanted</span>
            <span className="text-2xl font-bold text-white" aria-hidden="true">{votes}</span>
          </label>
          <input
            ref={firstRef}
            id="boost-votes"
            type="range"
            min={BOOST_MIN}
            max={BOOST_MAX}
            step={1}
            value={votes}
            onChange={(e) => { setVotes(clampVotes(e.target.value)); setError(null); }}
            aria-valuetext={plural(votes, 'vote')}
            className="mt-2 w-full accent-voto-coral"
          />
          <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Quick picks">
            {QUICK.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => { setVotes(q); setError(null); }}
                aria-pressed={votes === q}
                className={`rounded-lg px-3 py-1 text-sm font-medium ring-1 transition ${
                  votes === q ? 'bg-voto-coral text-white ring-voto-coral' : 'bg-voto-surface text-voto-fg ring-voto-line hover:bg-voto-raised'
                }`}
              >
                {q}
              </button>
            ))}
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-2 text-sm">
            <div className="rounded-xl bg-voto-surface px-3 py-2">
              <dt className="text-xs text-voto-muted">Cost</dt>
              <dd className="font-semibold text-white" data-testid="boost-cost">{plural(cost, 'credit')}</dd>
            </div>
            <div className="rounded-xl bg-voto-surface px-3 py-2">
              <dt className="text-xs text-voto-muted">Your balance</dt>
              <dd className="font-semibold text-white" data-testid="boost-balance">
                {balance === null ? (balanceError ? '—' : '…') : plural(balance, 'credit')}
              </dd>
            </div>
          </dl>

          {(error?.kind === 'credits' || (short && !error)) && (
            <p role="alert" className="mt-3 rounded-xl bg-voto-coral/10 px-3 py-2 text-sm text-voto-coral">
              Not enough credits
              {error?.needed ? ` — you need ${formatCount(error.needed)}` : ''}.{' '}
              <Link to="/credits" className="font-semibold underline">Get credits</Link>
            </p>
          )}
          {error?.kind === 'auth' && (
            <p role="alert" className="mt-3 text-sm text-voto-coral">
              <Link to="/auth" className="font-semibold underline">Sign in</Link> to boost.
            </p>
          )}
          {error?.kind === 'other' && (
            <p role="alert" className="mt-3 text-sm text-voto-coral">Couldn’t boost right now. Try again.</p>
          )}

          <div className="mt-4 flex gap-2.5">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-[14px] bg-voto-coral px-4 py-3 text-[15px] font-semibold text-white transition hover:bg-voto-coralDeep disabled:opacity-60"
            >
              {submitting ? 'Boosting…' : `Boost for ${plural(cost, 'credit')}`}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-[14px] bg-voto-surface px-4 py-3 text-[15px] font-semibold text-voto-fg ring-1 ring-voto-line hover:bg-voto-raised"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
